import { GatsbyImage, getImage } from "gatsby-plugin-image";
import React, { useEffect, useState } from "react";
import {
	Button as BootstrapButton,
	Card,
	Col,
	Container,
	Modal,
	Row,
} from "react-bootstrap";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { FaLinkedin } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import Button from "./Button";
import { SafeHtmlParser } from "./SafeHtmlParser";

const Team2 = ({ heading, body, teamMembers, secondHeading, button }) => {
	const [selectedMember, setSelectedMember] = useState(null);
	const [visibleCount, setVisibleCount] = useState(6); // Default for medium screens
	const [isDesktop, setIsDesktop] = useState(false); // State to track desktop view

	// Adjust visibleCount based on screen size
	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth >= 992) {
				setVisibleCount(teamMembers.length); // Show all for desktop
				setIsDesktop(true);
			} else if (window.innerWidth >= 768) {
				setVisibleCount(6); // Show 6 for medium screens
				setIsDesktop(false);
			} else {
				setVisibleCount(3); // Show 3 for small screens
				setIsDesktop(false);
			}
		};

		handleResize(); // Initial call
		window.addEventListener("resize", handleResize); // Add resize listener
		return () => window.removeEventListener("resize", handleResize); // Cleanup
	}, [teamMembers.length]);

	const handleShowModal = (member) => {
		setSelectedMember(member);
	};

	const handleCloseModal = () => {
		setSelectedMember(null);
	};

	const loadMore = () => {
		setVisibleCount(teamMembers.length); // Show all members
	};

	return (
		<section className="py-5 py-lg-7">
			<Container>
				{/* Heading Section */}
				<Row className="mb-4 mb-md-6">
					<Col>
						<div className="text-primary">
							<SafeHtmlParser htmlContent={heading} />
						</div>
					</Col>
				</Row>

				{/* Team Members Grid */}
				<Row className="g-5">
					{teamMembers.slice(0, visibleCount).map((member, index) => {
						const image = getImage(
							member.teamMemberFields.image?.localFile?.childImageSharp
						);

						return (
							<Col md={4} xs={12} xl={3} key={index} className="mb-4">
								<Card className="h-100 p-0 border-0">
									<Card.Body className="p-0">
										{image && (
											<GatsbyImage
												image={image}
												alt={member.teamMemberFields.name}
												className="rounded-circle mb-3"
												style={{
													width: "100px",
													height: "100px",
													objectFit: "cover",
												}}
											/>
										)}
										<h5 className="text-primary mb-0 pb-0 fs-4">
											{member.teamMemberFields.name}
										</h5>
										<p className=" mb-3">{member.teamMemberFields.position}</p>
										<p>{member.teamMemberFields.shortDescription}</p>
										<a
											style={{ cursor: "pointer" }}
											className="p-0 outfit-bold text-primary"
											onClick={() => handleShowModal(member)}
										>
											Read More
										</a>
										<div className="d-flex gap-3 mt-3">
											{member.teamMemberFields.linkedinLink && (
												<a
													href={member.teamMemberFields.linkedinLink}
													target="_blank"
													rel="noopener noreferrer"
													className="text-dark"
												>
													<FaLinkedin size={20} />
												</a>
											)}
											{/* {member.teamMemberFields.twitterLink && (
												<a
													href={member.teamMemberFields.twitterLink}
													target="_blank"
													rel="noopener noreferrer"
													className="text-dark"
												>
													<FaXTwitter size={20} />
												</a>
											)} */}
										</div>
									</Card.Body>
								</Card>
							</Col>
						);
					})}
				</Row>

				{/* View All Button */}
				{!isDesktop && visibleCount < teamMembers.length && (
					<Row className="mt-4">
						<Col className="text-center">
							<BootstrapButton
								style={{ borderRadius: "0px" }}
								className={`py-2 px-4 w-100 w-md-auto`}
								variant="outline-primary"
								onClick={loadMore}
							>
								View All
							</BootstrapButton>
						</Col>
					</Row>
				)}

				{/* Additional Section */}
				<Row className="mt-5 mt-lg-7">
					<Col>
						<div className="text-primary">
							<SafeHtmlParser htmlContent={secondHeading} />
						</div>
						{body?.length > 0 && <SafeHtmlParser htmlContent={body} />}
						{button && (
							<Button
								width="w-100 w-md-auto"
								colour="#00304B"
								text={button.title}
								link={button.url}
								isOutline={true}
							/>
						)}
					</Col>
				</Row>
			</Container>

			{/* Modal for Detailed Team Member View */}
			<Modal
				show={!!selectedMember}
				onHide={handleCloseModal}
				centered
				size="xl"
				dialogClassName="wide-modal"
			>
				{selectedMember && (
					<Modal.Body className="p-lg-5 p-3 position-relative">
						<div className="position-absolute end-0 top-0 p-lg-5 p-3">
							<IoMdClose
								onClick={handleCloseModal}
								style={{ cursor: "pointer" }}
								className="fs-4"
							/>
						</div>
						<div className="d-flex mb-4 align-items-center gap-3">
							{selectedMember.teamMemberFields.image && (
								<GatsbyImage
									image={getImage(
										selectedMember.teamMemberFields.image.localFile
											.childImageSharp
									)}
									alt={selectedMember.teamMemberFields.name}
									className="rounded-circle"
									style={{ width: "80px", height: "80px" }}
								/>
							)}
							<div>
								<h2 className=" pb-0 mb-1">
									{selectedMember.teamMemberFields.name}
								</h2>
								<p style={{ fontSize: "95%" }} className="text-primary mb-0">
									{selectedMember.teamMemberFields.position}
								</p>
							</div>
						</div>
						<SafeHtmlParser
							htmlContent={selectedMember.teamMemberFields.body}
						/>

						{/* Contact Information */}
						<div className="mt-4">
							{selectedMember.teamMemberFields.phone?.title && (
								<p className="d-flex align-items-center">
									<FaPhoneAlt className="me-2 text-primary" />
									<a
										href={selectedMember.teamMemberFields.phone.url}
										className="text-dark text-decoration-none"
									>
										{selectedMember.teamMemberFields.phone.title}
									</a>
								</p>
							)}
							{selectedMember.teamMemberFields.email?.title && (
								<p className="d-flex align-items-center">
									<FaEnvelope className="me-2 text-primary" />
									<a
										href={selectedMember.teamMemberFields.email.url}
										className="text-dark text-decoration-none"
									>
										{selectedMember.teamMemberFields.email.title}
									</a>
								</p>
							)}

							{/* Social Links */}
							<div className="d-flex gap-3 mt-3">
								{selectedMember.teamMemberFields.linkedinLink && (
									<a
										href={selectedMember.teamMemberFields.linkedinLink}
										target="_blank"
										rel="noopener noreferrer"
										className="text-dark"
									>
										<FaLinkedin size={20} />
									</a>
								)}
								{/* {selectedMember.teamMemberFields.twitterLink && (
									<a
										href={selectedMember.teamMemberFields.twitterLink}
										target="_blank"
										rel="noopener noreferrer"
										className="text-dark"
									>
										<FaXTwitter size={20} />
									</a>
								)} */}
							</div>
						</div>
					</Modal.Body>
				)}
			</Modal>
		</section>
	);
};

export default Team2;
